import React, { createContext, useContext, useState, useEffect } from "react";

type BreadcrumbType = "organizations" | "locations" | "machines";

export interface BreadcrumbEntry {
  path: string;
  breadcrumb: string;
  type?: BreadcrumbType;
  currentId?: number;
  locationId?: number; // For machines type to filter by location
  isLoading?: boolean; // Loading state for the breadcrumb item
  /**
   * Handle a dropdown pick in-page instead of navigating. Use on pages whose
   * subject lives in component state rather than the URL (e.g. `/map`, where the
   * location is selected on the page and the path carries no id to swap).
   *
   * MUST be identity-stable: `useSetBreadcrumbs` diffs entries with
   * `JSON.stringify`, which drops functions, so a callback that changes identity
   * is never re-published and the stored crumb keeps calling a stale closure.
   * Wrap it in a ref-backed `useCallback`.
   */
  onSelect?: (id: number) => void;
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbEntry[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbEntry[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const BreadcrumbProvider = ({ children }: { children: React.ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([]);

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbs must be used within a BreadcrumbProvider");
  }
  return context;
};

/**
 * Hook to set breadcrumbs for the current page
 * Automatically clears breadcrumbs when component unmounts
 *
 * @example
 * ```tsx
 * useSetBreadcrumbs([
 *   { path: "/organizations", breadcrumb: "Organizations" },
 *   { path: `/organizations/${id}`, breadcrumb: orgName, type: "organizations", currentId: id }
 * ]);
 * ```
 */
export const useSetBreadcrumbs = (breadcrumbs: BreadcrumbEntry[]) => {
  const { setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);

    // Clear breadcrumbs when component unmounts
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(breadcrumbs), setBreadcrumbs]);
};
